import styled from '@emotion/styled'
import React from 'react'

import { debounce } from '@livechat/data-utils'

import Fieldset from 'components/fields/Fieldset'
import Input from 'components/fields/Input'
import Label from 'components/fields/Label'
import { isValidHexColor } from 'lib/helpers'

const Wrapper = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	padding-left: ${({ theme }) => theme.spaces.space1};

	${Fieldset} {
		padding: 0;
		padding-bottom: ${({ theme }) => theme.spaces.space4};
	}

	${Label} {
		${({ theme }) => theme.typography.caption}
	}
`

const ColorPreview = styled.span<{ fill?: string }>`
	position: absolute;
	height: 32px;
	width: 32px;
	background-color: ${({ fill }) => fill};
	border-radius: ${({ theme }) => theme.borderRadiuses.sm};
`

const HiddenInput = styled.input`
	position: absolute;
	height: 32px;
	width: 32px;
	opacity: 0;
	z-index: ${({ theme }) => theme.zIndexes.high};
	cursor: pointer;

	&:focus-visible + span {
		outline: 1px solid ${({ theme }) => theme.colors.light.accent};
		outline-offset: 2px;
	}
`

const ColorWrapper = styled.div`
	position: absolute;
	bottom: 16px;
	left: 8px;
	z-index: ${({ theme }) => theme.zIndexes.above};
	height: 32px;
	width: 32px;
`

const TextInput = styled(Input)`
	height: 100%;
	min-height: 44px;
	padding: ${({ theme }) => theme.spaces.space5};
	padding-left: 44px;
	width: 130px;
	text-transform: uppercase;
	z-index: ${({ theme }) => theme.zIndexes.none};
`

type Props = {
	label: string
	value: string
	onChange: (color: string) => void
	onBlur?: (event: React.FocusEvent) => void
	onFocus?: (event: React.FocusEvent) => void
}

const CustomColorInput: React.FC<Props> = ({ label, value, onChange, onFocus, onBlur }) => {
	const onChangeDebounced = React.useMemo(() => debounce(50, onChange), [onChange])
	const isValid = isValidHexColor(value)

	return (
		<Wrapper>
			<ColorWrapper>
				<HiddenInput
					aria-label={`${label} color picker`}
					type="color"
					value={value}
					onChange={(event) => onChangeDebounced(event.target.value)}
					onFocus={onFocus}
					onBlur={onBlur}
				/>
				<ColorPreview fill={isValid ? value : undefined} />
			</ColorWrapper>
			<TextInput
				label={label}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				onFocus={onFocus}
				onBlur={onBlur}
				{...(!isValid && { error: 'Provide valid hex color' })}
			/>
		</Wrapper>
	)
}

export default CustomColorInput
